import axios from "axios";

// We ALWAYS prefer the build-time `REACT_APP_BACKEND_URL` so preview keeps
// pointing at the preview backend, BUT — when this bundle is served from a
// custom production domain (Cloudflare-proxied) the env var baked at build
// time may still point at the preview host. Calling preview from production
// is a cross-origin request → Safari ITP / SameSite quirks reject the auth
// cookie → user gets bounced. To stay robust, fall back to the page origin
// whenever the env var clearly mismatches it. This keeps every fetch
// same-origin, cookies are auto-attached, and CORS is irrelevant.
const ENV_URL = process.env.REACT_APP_BACKEND_URL;
let resolvedBackendUrl = ENV_URL || "";
if (typeof window !== "undefined" && window.location && window.location.origin) {
  const pageOrigin = window.location.origin;
  if (!ENV_URL || !ENV_URL.startsWith(pageOrigin)) {
    resolvedBackendUrl = pageOrigin;
  }
}
const BACKEND_URL = resolvedBackendUrl;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Manager scope (admin/superadmin only — employees/managers never hit this).
// Reads localStorage `clc_mgr_site` ("ALL"|"SITE_1"|"SITE_2") and appends
// `?site=` to every `/manager/*` call so the same screens render scoped data.
// MANAGER role ignores this header — backend pins them to their primary_site.
//
// SECURITY NOTE — the only data this module persists in localStorage is the
// admin's chosen site filter. NO TOKENS or credentials live here: the auth
// session is carried by an httpOnly `clc_session` cookie set by the server
// (axios `withCredentials: true` above sends it automatically). A site name
// is not sensitive, so XSS-via-localStorage is not a risk vector.
const MGR_SITE_KEY = "clc_mgr_site";
export const getMgrSite = () => {
  if (typeof window === "undefined") return "ALL";
  return window.localStorage.getItem(MGR_SITE_KEY) || "ALL";
};
export const setMgrSite = (site) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MGR_SITE_KEY, site);
};

api.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.startsWith("/manager/") || url.startsWith("manager/")) {
    const site = getMgrSite();
    if (site && !("site" in (config.params || {}))) {
      config.params = { ...(config.params || {}), site };
    }
  }
  return config;
});

// Paths where 401 is expected (no redirect) — auth endpoints themselves.
const AUTH_PATHS = ["/auth/login", "/auth/login-staff", "/auth/me", "/auth/logout"];

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const code = err?.response?.data?.error; // "geo_restricted" | "wifi_restricted"
    const isAuthCall = AUTH_PATHS.some((p) => url.includes(p));
    if (typeof window !== "undefined") {
      // 403 from the tiered access-control middleware — send user to the
      // right friendly page so they know what to do next.
      if (status === 403 && code === "geo_restricted" && window.location.pathname !== "/access-restricted") {
        window.location.replace("/access-restricted");
        return Promise.reject(err);
      }
      // wifi_restricted is handled inline on the clock page (no hard redirect).
      if ((status === 401 || status === 423) && !isAuthCall) {
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }
    }
    return Promise.reject(err);
  },
);
